/**
 * The largest type size at which a whole wall is on the screen at once.
 *
 * A tile's floor is 80 columns by 24 rows (`--k-pane-cols` / `--k-pane-rows`),
 * and that floor is why the wall scrolls: six panes at a readable size are
 * taller than any column this console has. The honest answer to "show me all of
 * them" is therefore not *make the tiles shorter* — that is the defect the floor
 * was written to end — but **make the cell smaller**. Twenty-four rows of 10px
 * type is a terminal; five rows of 15px type is a porthole.
 *
 * So this searches downwards from the operator's own size for the first one at
 * which every tile still clears the floor *and* the whole grid is inside its
 * box. Measured in the browser against the deployment this was written for — an
 * 1800×1138 window with the telemetry rail folded, which gives the wall a
 * 1404 × 998 box and a 12px gap — from a 15px type size:
 *
 * | panes | fitted | grid  | each terminal | overflow before |
 * |-------|--------|-------|---------------|-----------------|
 * | 1–2   | 15     | 2 × 1 | 83 × 25       | none            |
 * | 3–4   | 10     | 2 × 2 | 125 × 25      | 392px           |
 * | 6     | 9.5    | 3 × 2 | 87 × 25       | 1099px          |
 * | 9+    | —      | —     | does not fit, and says so             |
 *
 * Read the two right-hand columns together. At six panes the operator had two
 * tiles on the screen, 83 columns wide, and 1099px of wall below the fold — of
 * which two tiles had not even mounted a terminal. Fitted, all six are live at
 * **87** columns: *wider* than before, and three times as many. Nobody would
 * guess that from a type-size setting, which is why it is a control and not a
 * note in the appearance panel.
 *
 * **Pure, and separated from the hook for the reason `geometry.ts` gives.** The
 * interesting cases here are arithmetic — a box that fits nothing, a ceiling
 * below the floor, a wall of one — and none of them needs a browser to ask
 * about.
 */

/**
 * The floor, taken apart far enough to do sums with.
 *
 * Read from the stylesheet rather than restated here: `--k-pane-min-w` is
 * `cols * font * widthRatio + widthBase`, and every one of those four is a token
 * (see `readFloorMetrics`). A second copy of that arithmetic in TypeScript is a
 * second place for it to be wrong, and this file exists precisely because the
 * first copy already was once — the height floor claimed 24 rows and delivered
 * 13 for the better part of a year.
 */
export interface FloorMetrics {
  /** `--k-pane-cols` × `--k-term-cell-w-ratio`: pixels of tile width per point of type. */
  widthPerFont: number
  /** `--k-pane-chrome-w`: the tile's width that the type size does not move. */
  widthBase: number
  /** `--k-pane-rows` × `--k-term-cell-h-ratio`. */
  heightPerFont: number
  /** `--k-pane-chrome`: header, border and the terminal's own padding. */
  heightBase: number
  /** The grid's gap, which is frame rather than type and so does not scale. */
  gap: number
}

export interface FitRequest {
  /** The box the tiles are laid out in, in CSS pixels. */
  box: { width: number; height: number }
  /** Grid cells the wall occupies — a tile spanning two columns takes two. */
  cells: number
  metrics: FloorMetrics
  /** The operator's own size. The fit never goes *above* it: this control is
   *  asked to make things fit, not to make them bigger than somebody chose. */
  ceiling: number
  /** The smallest this console will draw a terminal at. Below this the type is
   *  not small, it is gone; `readTermFontSize` refuses anything under 9 for the
   *  same reason. */
  floor?: number
  /** How finely to step between the two. */
  step?: number
}

export interface Fit {
  /** The size to draw at. */
  font: number
  /** False when even `floor` leaves the wall taller than its box — which is the
   *  honest answer for a wall of six on a laptop, and has to be said rather than
   *  hidden behind a type size nobody can read. */
  fits: boolean
  /** How the tiles land at `font`. Not decoration: it is what the control says
   *  out loud, so an operator can see what the size bought them. */
  columns: number
  rows: number
}

/** Below this the type is not small, it is gone. */
export const FONT_FLOOR = 9

/** Fine enough to find the boundary, coarse enough that the search is a dozen
 *  steps rather than a hundred — every step is arithmetic, but the *result* is a
 *  tmux resize on every pane, so the scan runs in full before anything is
 *  applied. */
const FONT_STEP = 0.5

/** Where the tiles land at one size. Exported for the tests, which assert the
 *  table in the comment above rather than restating the formula. */
export function layoutAt(font: number, req: FitRequest): { columns: number; rows: number; height: number } {
  const { metrics, box, cells } = req
  const minW = metrics.widthPerFont * font + metrics.widthBase
  const minH = metrics.heightPerFont * font + metrics.heightBase
  // `auto-fit` with `minmax(min(minW, 100%), 1fr)`: as many whole tracks as fit,
  // and never fewer than one — a column narrower than a single tile still draws
  // the tile, clipped, rather than drawing none.
  const columns = Math.max(1, Math.floor((box.width + metrics.gap) / (minW + metrics.gap)))
  const rows = Math.max(1, Math.ceil(cells / columns))
  return { columns, rows, height: rows * minH + (rows - 1) * metrics.gap }
}

export function fitFont(req: FitRequest): Fit {
  const floor = req.floor ?? FONT_FLOOR
  const step = req.step ?? FONT_STEP
  // Nothing on the wall is not a fitting problem. Answering `floor` here would
  // shrink every terminal in the console the moment the last pane was detached.
  if (req.cells <= 0) return { font: req.ceiling, fits: true, columns: 1, rows: 0 }

  const top = Math.max(req.ceiling, floor)
  const steps = Math.max(0, Math.ceil((top - floor) / step))
  for (let i = 0; i <= steps; i++) {
    // Counted from the ceiling rather than decremented, so a long scan cannot
    // drift off the half-point grid and land on 11.399999999999999.
    const font = Math.max(floor, Math.round((top - i * step) * 100) / 100)
    const at = layoutAt(font, req)
    if (at.height <= req.box.height) {
      return { font, fits: true, columns: at.columns, rows: at.rows }
    }
  }
  const at = layoutAt(floor, req)
  return { font: floor, fits: false, columns: at.columns, rows: at.rows }
}

/**
 * The floor's own figures, from the stylesheet that enforces it.
 *
 * `element` is the grid itself, so the gap comes from the rule that draws it
 * rather than from a token this would have to pick the right density of. The
 * rest are read as custom properties, which is why they are plain numbers in
 * `tokens.css`: a custom property's computed value keeps its units unresolved,
 * so `--k-pane-chrome: 65px` reads back as the string `65px` and
 * `--k-frame-gap: 0.5rem` reads back as `0.5rem`. Numbers are the only shape
 * that survives the round trip.
 *
 * Returns null when anything is missing rather than defaulting: a fit computed
 * from a guessed floor would move every terminal to a size derived from nothing.
 */
export function readFloorMetrics(element: Element): FloorMetrics | null {
  const cs = getComputedStyle(element)
  const num = (name: string): number | null => {
    const raw = cs.getPropertyValue(name).trim()
    if (raw === '') return null
    const n = Number.parseFloat(raw)
    return Number.isFinite(n) ? n : null
  }
  const cols = num('--k-pane-cols')
  const rows = num('--k-pane-rows')
  const wRatio = num('--k-term-cell-w-ratio')
  const hRatio = num('--k-term-cell-h-ratio')
  const wBase = num('--k-pane-chrome-w')
  const hBase = num('--k-pane-chrome')
  // `rowGap` is a resolved used value, so it arrives in pixels whatever unit the
  // token was written in. `normal` is what a non-grid element answers.
  const gap = Number.parseFloat(cs.rowGap)
  if (cols === null || rows === null || wRatio === null || hRatio === null) return null
  if (wBase === null || hBase === null) return null
  return {
    widthPerFont: cols * wRatio,
    widthBase: wBase,
    heightPerFont: rows * hRatio,
    heightBase: hBase,
    gap: Number.isFinite(gap) ? gap : 0,
  }
}
